// src/pages/Shows.jsx
import React, { useEffect, useState, useMemo } from 'react'
import { db } from '../firebase'
import { collection, getDocs, orderBy, query } from 'firebase/firestore'
import { Link, useNavigate } from 'react-router-dom'
import { ProssimiShowBanner } from '../components/show/ProssimiShowBanner'
import SectionAd from '../components/ads/SectionAd'

const SHOW_TYPE_PRIORITY = {
  ple: 0,
  'ple-free': 1,
  'weekly-special': 2,
  weekly: 3
}

const SHOW_TYPE_BADGE_META = {
  ple: {
    className: 'text-brand-primary bg-brand-secondary',
    label: 'PLE',
    shortLabel: 'PLE'
  },
  'ple-free': {
    className: 'text-light bg-purple',
    label: 'Special',
    shortLabel: 'Special'
  },
  weekly: {
    className: 'text-light bg-brand-primary',
    label: 'Settimanale',
    shortLabel: 'Sett.'
  },
  'weekly-special': {
    className: 'text-brand-primary bg-brand-primary-ultralight',
    label: 'Speciale TV',
    shortLabel: 'TV Spec.'
  }
}

const getShowTypeBadgeMeta = showType =>
  SHOW_TYPE_BADGE_META[showType] || {
    className: 'text-light bg-secondary',
    label: showType || 'Show',
    shortLabel: showType || 'Show'
  }

const compareShowsForCalendar = (firstShow, secondShow) => {
  const firstPriority = SHOW_TYPE_PRIORITY[firstShow?.type || 'weekly'] ?? 99
  const secondPriority = SHOW_TYPE_PRIORITY[secondShow?.type || 'weekly'] ?? 99

  if (firstPriority !== secondPriority) {
    return firstPriority - secondPriority
  }

  return (firstShow?.nome || '').localeCompare(secondShow?.nome || '', 'it')
}

function Shows() {
  const [shows, setShows] = useState([])
  const [filter, setFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('calendar')
  const navigate = useNavigate()
  const [upcomingShows, setUpcomingShows] = useState([])
  const [filterRoster, setFilterRoster] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [modalShow, setModalShow] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedShows, setSelectedShows] = useState([])

  // Logo per show settimanali placeholder
  const weeklyLogos = {
    Raw: 'https://www.wwe.com/f/styles/wwe_show_logo_network_l/public/all/2024/12/RAW_Primary_Logo_For_Any_Background_(1)--de3612b9543981440a24489e0e568d9b.png',
    SmackDown:
      'https://www.wwe.com/f/styles/wwe_show_logo_network_l/public/all/2024/09/Smackdown_LOGO--c8e0b09904cb1356d309a8ea2acb0422.png',
    NXT: 'https://www.wwe.com/f/styles/wwe_show_logo_network_l/public/all/2024/10/NXTonCW--0f06792893241464479338f55596a5bf.png',
    TNA: 'https://tnawrestling.com/assets/img/logo.webp',
    AAA: 'https://www.wwe.com/f/styles/wwe_show_logo_l/public/2025/08/AAA_Channel_Logo_Color.png'
  }

  const calendarWeekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
  /*
    { short: 'Lun', long: 'Lunedì' },
    { short: 'Mar', long: 'Martedì' },
    { short: 'Mer', long: 'Mercoledì' },
    { short: 'Gio', long: 'Giovedì' },
    { short: 'Ven', long: 'Venerdì' },
    { short: 'Sab', long: 'Sabato' },
    { short: 'Dom', long: 'Domenica' }
  */

  // Funzione per normalizzare le date (rimuove l'ora)
  const normalizeDate = date => {
    if (!date) return null
    const normalized = new Date(date)
    normalized.setHours(12, 0, 0, 0)
    return normalized
  }

  // Funzione per ottenere la chiave della data (YYYY-MM-DD)
  const getDateKey = date => {
    if (!date) return ''
    const normalized = normalizeDate(date)
    return normalized.toISOString().split('T')[0]
  }

  // Funzione per formattare la data in italiano
  const formatDate = date => {
    if (!date) return 'Data non disponibile'
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Funzione per formattare la data completa
  const formatFullDate = date => {
    if (!date) return ''
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const getWeeklyPlaceholder = date => {
    if (date.getDay() === 1) return { type: 'Raw', logo: weeklyLogos.Raw }
    if (date.getDay() === 2) return { type: 'NXT', logo: weeklyLogos.NXT }
    if (date.getDay() === 5) return { type: 'SmackDown', logo: weeklyLogos.SmackDown }
    if (date.getDay() === 6) return { type: 'AAA', logo: weeklyLogos.AAA }
    return null
  }

  useEffect(() => {
    const fetchShows = async () => {
      try {
        // Recupero TUTTI gli show (passati e futuri)
        const q = query(collection(db, 'shows'), orderBy('data', 'desc'))

        const querySnapshot = await getDocs(q)
        const showsData = querySnapshot.docs.map(doc => {
          const data = doc.data()
          const rawDate = data.data?.toDate()

          // Normalizza la data
          const normalizedDate = normalizeDate(rawDate)

          return {
            id: doc.id,
            ...data,
            date: normalizedDate
          }
        })

        // Per ogni show, conto i partecipanti
        const showsWithCounts = await Promise.all(
          showsData.map(async show => {
            const pSnap = await getDocs(
              collection(db, 'shows', show.id, 'participations')
            )
            return {
              ...show,
              participantsCount: pSnap.size
            }
          })
        )

        // Separa show passati e futuri
        const now = new Date()
        now.setHours(12, 0, 0, 0) // Normalizza anche la data corrente

        const pastShows = showsWithCounts.filter(
          show => show.date && show.date <= now
        )
        const upcoming = showsWithCounts.filter(
          show => show.date && show.date > now
        )

        setUpcomingShows(upcoming)
        setShows(pastShows)
      } catch (error) {
        console.error('Error fetching shows:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchShows()
  }, [])

  // Filtra gli show
  const filteredShows = useMemo(() => {
    return shows.filter(show => {
      if (filter === 'all' && filterRoster === 'all' && !searchTerm) return true

      const typeMatch = filter === 'all' || show.type === filter
      const rosterMatch =
        filterRoster === 'all' ||
        (filterRoster === 'main' && !show.isExtra) ||
        (filterRoster === 'extra' && show.isExtra)

      const searchMatch =
        !searchTerm ||
        show.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (show.description &&
          show.description.toLowerCase().includes(searchTerm.toLowerCase()))

      return typeMatch && rosterMatch && searchMatch
    })
  }, [shows, filter, filterRoster, searchTerm])

  const allShows = useMemo(() => [...shows, ...upcomingShows], [shows, upcomingShows])

  const showsByDate = useMemo(() => {
    const groupedShows = new Map()

    allShows.forEach(show => {
      if (!show.date) return

      const key = getDateKey(show.date)
      if (!groupedShows.has(key)) {
        groupedShows.set(key, [])
      }

      groupedShows.get(key).push(show)
    })

    groupedShows.forEach(showsForDay => {
      showsForDay.sort(compareShowsForCalendar)
    })

    return groupedShows
  }, [allShows, getDateKey])

  // Funzioni per il calendario
  const navigateMonth = direction => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + direction, 1))
  }

  // Calcola i giorni del mese per il calendario
  const calendarWeeks = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()

    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const startOffset = (firstDayOfMonth.getDay() + 6) % 7
    const totalDays = lastDayOfMonth.getDate()
    const previousMonthLastDay = new Date(year, month, 0).getDate()
    const todayKey = getDateKey(new Date())

    const buildCalendarCell = date => {
      const normalizedDate = normalizeDate(date)
      const key = getDateKey(normalizedDate)
      const showsForDay = [...(showsByDate.get(key) || [])]
      const isPast = key < todayKey
      const placeholder = !isPast && showsForDay.length === 0
        ? getWeeklyPlaceholder(normalizedDate)
        : null

      return {
        key,
        day: normalizedDate.getDate(),
        date: normalizedDate,
        shows: showsForDay,
        placeholder,
        isOutsideMonth: normalizedDate.getMonth() !== month,
        isToday: key === todayKey,
        weekdayShort: calendarWeekdayLabels[(normalizedDate.getDay() + 6) % 7] || ''
      }
    }

    const days = []

    // Giorni del mese precedente per completare la prima settimana
    for (let i = 0; i < startOffset; i++) {
      const day = previousMonthLastDay - startOffset + i + 1
      days.push(buildCalendarCell(new Date(year, month - 1, day)))
    }

    // Giorni del mese
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day)
      days.push(buildCalendarCell(date))
      /*
            const key = getDateKey(date)
      
            // Prendi tutti gli show per questo giorno (passati + futuri)
            let showsForDay = [...shows, ...upcomingShows].filter(show => {
              if (!show.date) return false
              return getDateKey(show.date) === key
            })
      
            // ORDINA: prima i PLE, poi i settimanali
            showsForDay.sort((a, b) => {
              if (a.type === 'ple' && b.type !== 'ple') return -1
              if (a.type !== 'ple' && b.type === 'ple') return 1
              return 0
            })
      
            // Placeholder per show ricorrenti settimanali (solo se nessuno show reale)
            const isMonday = date.getDay() === 1
            const isTuesday = date.getDay() === 2
            const isFriday = date.getDay() === 5
            const isSaturday = date.getDay() === 6
      
            const getShowType = () => {
              if (isMonday) return { type: 'Raw', logo: weeklyLogos.Raw }
              if (isTuesday) return { type: 'NXT', logo: weeklyLogos.NXT }
              if (isSaturday) return { type: 'AAA', logo: weeklyLogos.AAA }
              if (isFriday) return { type: 'SmackDown', logo: weeklyLogos.SmackDown }
              return null
            }
      
            const showInfo = getShowType()
            let placeholder = null
            if (showsForDay.length === 0 && showInfo) {
              placeholder = {
                type: showInfo.type,
                logo: showInfo.logo
              }
            }
      
            days.push({
              type:
                showsForDay.length > 0 ? 'show' : placeholder ? 'placeholder' : 'day',
              day,
              date,
              shows: showsForDay, // già ordinato
              placeholder
            })
      */
    }

    let nextMonthDay = 1
    while (days.length % 7 !== 0) {
      days.push(buildCalendarCell(new Date(year, month + 1, nextMonthDay)))
      nextMonthDay += 1
    }

    return {
      year,
      month,
      days,
      totalDays,
      firstDayOfMonth,
      lastDayOfMonth
    }
  }, [calendarWeekdayLabels, currentMonth, getDateKey, getWeeklyPlaceholder, showsByDate])

  const goToCurrentMonth = () => {
    const today = new Date()
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1))
  }

  // Funzione per gestire il click su una cella del calendario
  const handleCellClick = cell => {
    if (cell.shows.length === 0) return

    const sortedShows = [...cell.shows].sort(compareShowsForCalendar)

    if (cell.shows.length === 1) {
      navigate(`/shows/${sortedShows[0].id}`)
    } else {
      setSelectedDate(cell.date)
      setSelectedShows(sortedShows)
      setModalShow(true)
    }
  }

  const calendarRows = useMemo(() => {
    const dayCells = Array.isArray(calendarWeeks?.days) ? calendarWeeks.days : []

    return Array.from(
      { length: Math.ceil(dayCells.length / 7) },
      (_, index) => dayCells.slice(index * 7, index * 7 + 7)
    )
  }, [calendarWeeks])

  const today = normalizeDate(new Date())

  return (
    <div className='shows-page' style={{ minHeight: '100vh' }}>
      <div className='shows-container'>
        {/* Header con titolo e toggle view */}
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mb-4 mt-3'>
          <h1
            className='ranking-title'
            style={{
              fontFamily: "'Cinzel Decorative'",
              textShadow: '1px 1px 2px rgba(0, 0, 0)'
            }}
          >
            Show
          </h1>

          <div className='btn-group btn-group-sm'>
            <button
              className={`btn ${viewMode === 'grid'
                ? 'btn-brand-secondary'
                : 'btn-outline-brand-secondary'
                }`}
              onClick={() => setViewMode('grid')}
              title='Griglia'
            >
              <i className='bi bi-grid-3x3-gap'></i>
              <span className='d-none d-md-inline ms-1'>Griglia</span>
            </button>
            <button
              className={`btn ${viewMode === 'calendar'
                ? 'btn-brand-secondary'
                : 'btn-outline-brand-secondary'
                }`}
              onClick={() => setViewMode('calendar')}
              title='Calendario'
            >
              <i className='bi bi-calendar3'></i>
              <span className='d-none d-md-inline ms-1'>Calendario</span>
            </button>
            <button
              className={`btn ${viewMode === 'list'
                ? 'btn-brand-secondary'
                : 'btn-outline-brand-secondary'
                }`}
              onClick={() => setViewMode('list')}
              title='Lista'
            >
              <i className='bi bi-list-task'></i>
              <span className='d-none d-md-inline ms-1'>Lista</span>
            </button>
          </div>
        </div>

        {viewMode !== 'calendar' && (
          <>
            {/* Barra di ricerca e reset */}
            <div className='shows-search-bar d-flex flex-column flex-md-row gap-2 mb-4'>
              <div className='flex-grow-1'>
                <div className='input-group input-group-sm shows-search-input-group'>
                  <span className='input-group-text bg-transparent shows-search-addon'>
                    <i className='bi bi-search text-brand-primary-ultralight'></i>
                  </span>
                  <input
                    type='text'
                    className='form-control bg-transparent shows-search-input text-light'
                    placeholder='Cerca show...'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className='btn btn-outline-brand-primary shows-search-clear'
                      onClick={() => setSearchTerm('')}
                    >
                      <i className='bi bi-x'></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Filtri compatti */}
            <div className='d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2'>
              <div
                className='btn-group btn-group-sm shows-filter-group'
                role='group'
                aria-label='Filtri show'
              >
                <button
                  className={`btn shows-filter-btn ${filter === 'all'
                    ? 'btn-brand-primary'
                    : 'btn-outline-brand-primary'
                    }`}
                  onClick={() => setFilter('all')}
                >
                  <span className='d-none d-sm-inline'>Tutti</span>
                  <span className='d-inline d-sm-none'>
                    <i className='bi bi-filter'></i>
                  </span>
                </button>
                <button
                  className={`btn shows-filter-btn ${filter === 'weekly'
                    ? 'btn-brand-primary'
                    : 'btn-outline-brand-primary'
                    }`}
                  onClick={() => setFilter('weekly')}
                >
                  <span className='d-none d-sm-inline'>Settimanali</span>
                  <span className='d-inline d-sm-none'>
                    <i className='bi bi-calendar-week'></i>
                  </span>
                </button>
                <button
                  className={`btn shows-filter-btn ${filter === 'ple'
                    ? 'btn-brand-secondary'
                    : 'btn-outline-brand-secondary'
                    }`}
                  onClick={() => setFilter('ple')}
                >
                  <span className='d-none d-sm-inline'>PLE</span>
                  <span className='d-inline d-sm-none'>
                    <i className='bi bi-award'></i>
                  </span>
                </button>
                <button
                  className={`btn shows-filter-btn ${filterRoster === 'extra'
                    ? 'btn-brand-accent'
                    : 'btn-outline-danger'
                    }`}
                  onClick={() =>
                    setFilterRoster(prev =>
                      prev === 'extra' ? 'all' : 'extra'
                    )
                  }
                >
                  <span className='d-none d-sm-inline'>Extra</span>
                  <span className='d-inline d-sm-none'>
                    <i className='bi bi-stars'></i>
                  </span>
                </button>
              </div>
            </div>

            {/* Banner prossimi show */}
            {!loading &&
              upcomingShows.length > 0 &&
              viewMode !== 'calendar' && (
                <ProssimiShowBanner shows={upcomingShows} />
              )}
          </>
        )}

        {/* Contenuto principale */}
        {loading ? (
          <div className='text-center py-5'>
            <div className='spinner-border text-brand-secondary' role='status'>
              <span className='visually-hidden'>Caricamento...</span>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          // Vista Griglia
          filteredShows.length === 0 ? (
            <div className='text-center py-5'>
              <i className='bi bi-calendar-x fs-1 text-brand-muted mb-3'></i>
              <p className='text-brand-muted'>Nessuno show trovato</p>
            </div>
          ) : (
            <div className='row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4'>
              {filteredShows.map(show => {
                const showBadge = getShowTypeBadgeMeta(show.type)

                return (
                  <div key={show.id} className='col'>
                    <Link
                      to={`/shows/${show.id}`}
                      className='card brand-card text-decoration-none h-100 border-0'
                      style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                      onMouseEnter={e =>
                        (e.currentTarget.style.transform = 'translateY(-4px)')
                      }
                      onMouseLeave={e =>
                        (e.currentTarget.style.transform = 'translateY(0)')
                      }
                    >
                      <div
                        className='card-img-top position-relative'
                        style={{
                          height: '150px',
                          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${show.sfondo})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        {show.logo && (
                          <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3'>
                            <img
                              src={show.logo}
                              alt={show.nome}
                              className='img-fluid h-100 w-auto'
                              style={{
                                objectFit: 'contain',
                                maxHeight: '80%',
                                maxWidth: '80%'
                              }}
                            />
                          </div>
                        )}

                        <span
                          className={`position-absolute top-0 end-0 m-2 badge ${showBadge.className}`}
                        >
                          {showBadge.label}
                        </span>

                        {show.isExtra && (
                          <span
                            className='position-absolute top-0 start-0 m-2'
                            title='Show Extra'
                            style={{ color: 'var(--brand-accent)' }}
                          >
                            <i className='bi bi-star-fill'></i>
                          </span>
                        )}

                        <small className='position-absolute bottom-0 start-0 m-1 badge bg-transparent text-light'>
                          <i className='bi bi-calendar-event me-1'></i>
                          {formatDate(show.date)}
                        </small>

                        <small className='position-absolute bottom-0 end-0 m-1 badge bg-transparent text-light'>
                          <i className='bi bi-people me-1'></i>
                          {show.participantsCount}
                        </small>
                      </div>
                    </Link>
                  </div>
                )
              })}
            </div>
          )
        ) : viewMode === 'list' ? (
          // Vista Lista
          filteredShows.length === 0 ? (
            <div className='text-center py-5'>
              <i className='bi bi-calendar-x fs-1 text-brand-muted mb-3'></i>
              <p className='text-brand-muted'>Nessuno show trovato</p>
            </div>
          ) : (
            <div className='card brand-card border-0 shadow-sm'>
              <div className='table-responsive'>
                <table className='table table-hover align-middle mb-0 table-dark'>
                  <thead className='brand-header'>
                    <tr>
                      <th className='ps-3'>Evento</th>
                      <th>Tipo</th>
                      <th>Data</th>
                      <th>Partecipanti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShows.map(show => {
                      const showBadge = getShowTypeBadgeMeta(show.type)

                      return (
                        <tr
                          key={show.id}
                          className='clickable-row'
                          onClick={() => navigate(`/shows/${show.id}`)}
                          style={{
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e =>
                            (e.currentTarget.style.background =
                              'rgba(38, 56, 72, 0.1)')
                          }
                          onMouseLeave={e =>
                            (e.currentTarget.style.background = '')
                          }
                        >
                          <td className='ps-3'>
                            <div className='d-flex align-items-center gap-2'>
                              <div
                                className='position-relative'
                                style={{ width: '50px', height: '30px' }}
                              >
                                <img
                                  src={show.sfondo}
                                  className='img-fluid w-100 h-100 object-fit-cover rounded'
                                  alt={show.nome}
                                />
                                {show.logo && (
                                  <img
                                    src={show.logo}
                                    className='position-absolute top-50 start-50 translate-middle'
                                    style={{
                                      maxHeight: '20px',
                                      maxWidth: '40px'
                                    }}
                                    alt='logo'
                                  />
                                )}
                              </div>
                              <span className='fw-medium text-light'>
                                {show.nome}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${showBadge.className}`}>
                              {showBadge.label}
                              {show.isExtra && ' (Extra)'}
                            </span>
                          </td>
                          <td className='text-light'>{formatDate(show.date)}</td>
                          <td>
                            <span className='fw-bold text-brand-secondary'>
                              {show.participantsCount ?? 0}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          // Vista Calendario
          <div className='shows-calendar-shell'>
            <div className='shows-calendar-toolbar'>

              <div className='shows-calendar-controls'>
                <button
                  type='button'
                  className='shows-calendar-nav'
                  onClick={() => navigateMonth(-1)}
                  aria-label='Vai al mese precedente'
                >
                  <i className='bi bi-chevron-left'></i>
                  <span>Prec.</span>
                </button>
                <button
                  type='button'
                  className='shows-calendar-nav shows-calendar-nav--today'
                  onClick={goToCurrentMonth}
                >
                  Oggi
                </button>
                <button
                  type='button'
                  className='shows-calendar-nav'
                  onClick={() => navigateMonth(1)}
                  aria-label='Vai al mese successivo'
                >
                  <span>Succ.</span>
                  <i className='bi bi-chevron-right'></i>
                </button>
              </div>
            </div>
            <div className='shows-calendar-toolbar__intro text-center mb-1'>
              <h4 className='shows-calendar-title mb-0 fw-bold text-brand-secondary'>
                {(() => {
                  const monthString = currentMonth.toLocaleDateString('it-IT', {
                    month: 'long',
                    year: 'numeric'
                  })
                  return monthString.charAt(0).toUpperCase() + monthString.slice(1)
                })()}
              </h4>
            </div>

            <div className='shows-calendar-board mb-3'>
              <div className='shows-calendar-weekdays'>
                {calendarWeekdayLabels.map(day => (
                  <div key={day} className='shows-calendar-weekdays__item'>
                    <span className='shows-calendar-weekdays__short'>{day}</span>
                  </div>
                ))}
              </div>

              {false && (
                <div className='shows-calendar-weeks'>
                  {calendarRows.map((week, weekIndex) => (
                    <section
                      key={`${calendarWeeks?.month || currentMonth.getMonth()}-${weekIndex}`}
                      className='shows-calendar-week'
                    >

                      <div className='shows-calendar-week__grid'>
                        {week.map((cell, index) => {
                          const today = normalizeDate(new Date())
                          const isPast = cell.date < today
                          const hasShows = cell.shows.length > 0
                          const hasMultipleShows = cell.shows.length > 1
                          const hasPlaceholder = !hasShows && cell.placeholder
                          const featuredShow = hasShows ? cell.shows[0] : null
                          const cellClassName = [
                            'shows-calendar-day',
                            cell.isToday ? 'shows-calendar-day--today' : '',
                            cell.isOutsideMonth ? 'shows-calendar-day--outside' : '',
                            isPast ? 'shows-calendar-day--past' : 'shows-calendar-day--future',
                            hasShows
                              ? 'shows-calendar-day--has-shows'
                              : hasPlaceholder
                                ? 'shows-calendar-day--placeholder'
                                : 'shows-calendar-day--empty'
                          ]
                            .filter(Boolean)
                            .join(' ')

                          return (
                            <article key={cell.key || `day-${cell.day}-${index}`} className={cellClassName}>
                              <div className='shows-calendar-day__header'>
                                <div className='shows-calendar-day__date-button'>
                                  <span className='shows-calendar-day__weekday'>
                                    {cell.weekdayShort}
                                  </span>
                                  <strong className='shows-calendar-day__number'>
                                    {cell.day}
                                  </strong>
                                </div>

                                <div className='shows-calendar-day__summary'>
                                  {cell.isToday && (
                                    <span className='shows-calendar-day__today-pill'>
                                      Oggi
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className='shows-calendar-day__body'>
                                {hasShows ? (
                                  <button
                                    type='button'
                                    className='shows-calendar-event'
                                    onClick={() => handleCellClick(cell)}
                                    aria-label={
                                      hasMultipleShows
                                        ? `Apri ${cell.shows.length} show del ${formatFullDate(cell.date)}`
                                        : `Apri ${featuredShow.nome}`
                                    }
                                    style={{
                                      backgroundImage: featuredShow?.sfondo
                                        ? `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.72)), url(${featuredShow.sfondo})`
                                        : 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-dark))'
                                    }}
                                  >
                                    <div className='shows-calendar-event__top'>
                                      <span
                                        className={`badge ${featuredShow?.type === 'ple'
                                          ? 'bg-brand-secondary text-brand-primary'
                                          : 'bg-brand-primary text-light'
                                          }`}
                                      >
                                        {featuredShow?.type === 'ple' ? 'PLE' : 'Sett.'}
                                      </span>

                                      <div className='shows-calendar-event__signals'>
                                        {featuredShow?.isExtra && (
                                          <span
                                            className='shows-calendar-event__extra'
                                            title='Show Extra'
                                          >
                                            <i className='bi bi-star-fill'></i>
                                          </span>
                                        )}
                                        {hasMultipleShows && (
                                          <span className='shows-calendar-event__stack'>
                                            <i className='bi bi-layers me-1'></i>
                                            +{cell.shows.length - 1}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className='shows-calendar-event__logo-wrap'>
                                      {featuredShow?.logo ? (
                                        <img
                                          src={featuredShow.logo}
                                          alt={featuredShow.nome}
                                          className='shows-calendar-event__logo'
                                        />
                                      ) : (
                                        <span className='shows-calendar-event__title'>
                                          {featuredShow?.type === 'ple' ? 'PLE' : 'Show'}
                                        </span>
                                      )}
                                    </div>

                                    <div className='shows-calendar-event__bottom'>
                                      <span className='shows-calendar-event__tap'>
                                        <i className='bi bi-hand-index-thumb me-1'></i>
                                      </span>
                                      <span className='shows-calendar-event__meta'>
                                        <i className='bi bi-people me-1'></i>
                                        {featuredShow?.participantsCount ?? 0}
                                      </span>
                                    </div>
                                  </button>
                                ) : hasPlaceholder ? (
                                  <div className='shows-calendar-placeholder'>
                                    {cell.placeholder.logo && (
                                      <img
                                        src={cell.placeholder.logo}
                                        alt={cell.placeholder.type}
                                        className='shows-calendar-placeholder__logo'
                                      />
                                    )}
                                    <span className='shows-calendar-placeholder__label'>
                                      {cell.placeholder.type}
                                    </span>
                                  </div>
                                ) : (
                                  <div className='shows-calendar-day__empty-state'>
                                    Nessuno show
                                  </div>
                                )}
                              </div>
                            </article>
                          )
                          /*
                                          if (cell.type === 'empty') {
                                            // Celle vuote (giorni di altri mesi) -> solo bordo bianco
                                            return (
                                              <div
                                                key={`empty-${index}`}
                                                className='calendar-cell empty'
                                                style={{
                                                  border: '1px solid rgba(255, 255, 255, 0.5)',
                                                  borderRadius: '12px',
                                                  background: 'transparent'
                                                }}
                                              />
                                            )
                                          }
                          
                                          const today = new Date()
                                          today.setHours(12, 0, 0, 0)
                                          const isToday = getDateKey(cell.date) === getDateKey(today)
                                          const isPast = cell.date < today
                                          const hasShows = cell.shows && cell.shows.length > 0
                                          const hasMultipleShows = hasShows && cell.shows.length > 1
                                          const hasPlaceholder = !hasShows && cell.placeholder
                          
                                          // Stili di base per tutte le celle (tranne empty)
                                          let cellStyle = {
                                            borderRadius: '12px',
                                            padding: '8px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            position: 'relative'
                                          }
                          
                                          // Stili specifici in base al contenuto
                                          if (hasShows) {
                                            // Evento reale: sfondo con immagine
                                            cellStyle = {
                                              ...cellStyle,
                                              backgroundImage: cell.shows[0].sfondo
                                                ? `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${cell.shows[0].sfondo})`
                                                : 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-dark))',
                                              backgroundSize: 'cover',
                                              backgroundPosition: 'center',
                                              cursor: 'pointer',
                                              border: 'none'
                                            }
                                          } else if (hasPlaceholder) {
                                            // Placeholder: sfondo trasparenza chiara, bordo tratteggiato bianco
                                            cellStyle = {
                                              ...cellStyle,
                                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                              border: '1px dashed rgba(255, 255, 255, 0.4)',
                                              backdropFilter: 'blur(2px)'
                                            }
                                          } else {
                                            // Giorno normale senza eventi: solo bordo bianco
                                            cellStyle = {
                                              ...cellStyle,
                                              border: '1px solid rgba(255, 255, 255, 0.3)',
                                              background: 'transparent'
                                            }
                                          }
                          
                                          if (isToday) {
                                            cellStyle = {
                                              ...cellStyle,
                                              border: `2px solid var(--brand-secondary, #ffc107)`,
                                              boxShadow:
                                                '0 0 0 2px rgba(255, 193, 7, 0.3), 0 0 0 4px rgba(0,0,0,0.2)',
                                              // Se la cella ha già uno sfondo (hasShows o placeholder), assicuriamoci che il bordo sia visibile
                                              ...(hasShows
                                                ? {
                                                    border: 'none',
                                                    outline: `2px solid var(--brand-secondary, #ffc107)`,
                                                    outlineOffset: '-1px'
                                                  }
                                                : {})
                                            }
                                          }
                                          return (
                                            <div
                                              key={`day-${cell.day}`}
                                              className={`calendar-cell ${isToday ? 'today' : ''} ${
                                                isPast ? 'past' : 'future'
                                              }`}
                                              onClick={() => handleCellClick(cell)}
                                              role={hasShows ? 'button' : undefined}
                                              tabIndex={hasShows ? 0 : -1}
                                              style={cellStyle}
                                            >
                                              <div className='align-self-start w-100'>
                                                <div
                                                  className={`calendar-day-number ${
                                                    hasShows ? 'text-white' : 'text-secondary'
                                                  }`}
                                                >
                                                  {cell.day}
                                                  {hasMultipleShows && (
                                                    <span className='badge ms-1 bg-brand-accent'>
                                                      +{cell.shows.length - 1}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                          
                                              <div className='flex-grow-1 d-flex align-items-center justify-content-center w-100'>
                                                {hasShows && cell.shows[0].logo && (
                                                  <img
                                                    src={cell.shows[0].logo}
                                                    alt={cell.shows[0].nome}
                                                    className='img-fluid'
                                                    style={{ maxHeight: '40px' }}
                                                  />
                                                )}
                                                {hasPlaceholder && cell.placeholder.logo && (
                                                  <img
                                                    src={cell.placeholder.logo}
                                                    alt={cell.placeholder.type}
                                                    className='img-fluid opacity-75'
                                                    style={{ maxHeight: '40px' }}
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          )
                          */
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}

              <div className='shows-calendar-grid'>
                {calendarWeeks.days.map((cell, index) => {
                  const isPast = cell.date < today
                  const hasShows = cell.shows.length > 0
                  const hasMultipleShows = cell.shows.length > 1
                  const hasPlaceholder = !hasShows && cell.placeholder
                  const featuredShow = hasShows ? cell.shows[0] : null
                  const featuredShowBadge = featuredShow
                    ? getShowTypeBadgeMeta(featuredShow.type)
                    : null
                  const cellClassName = [
                    'shows-calendar-day',
                    cell.isToday ? 'shows-calendar-day--today' : '',
                    cell.isOutsideMonth ? 'shows-calendar-day--outside' : '',
                    isPast ? 'shows-calendar-day--past' : 'shows-calendar-day--future',
                    hasShows
                      ? 'shows-calendar-day--has-shows'
                      : hasPlaceholder
                        ? 'shows-calendar-day--placeholder'
                        : 'shows-calendar-day--empty'
                  ]
                    .filter(Boolean)
                    .join(' ')
                  const cellStyle = hasShows
                    ? {
                        backgroundImage: featuredShow?.sfondo
                          ? `url(${featuredShow.sfondo})`
                          : 'linear-gradient(145deg, rgba(38, 56, 72, 0.94), rgba(18, 24, 31, 0.98))',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover'
                      }
                    : undefined

                  return (
                    <article
                      key={cell.key || `day-${cell.day}-${index}`}
                      className={cellClassName}
                      style={cellStyle}
                    >
                      <div className='shows-calendar-day__chrome'>
                        <span className='shows-calendar-day__number'>{cell.day}</span>
                        {hasMultipleShows && (
                          <button
                            type='button'
                            className='shows-calendar-day__stack'
                            onClick={() => handleCellClick(cell)}
                            aria-label={`Apri gli altri ${cell.shows.length - 1} show del ${formatFullDate(cell.date)}`}
                          >
                            +{cell.shows.length - 1}
                          </button>
                        )}
                      </div>

                      <div className='shows-calendar-day__body'>
                        {hasShows ? (
                          <button
                            type='button'
                            className='shows-calendar-tile shows-calendar-tile--featured'
                            onClick={() => handleCellClick(cell)}
                            aria-label={
                              hasMultipleShows
                                ? `Apri ${cell.shows.length} show del ${formatFullDate(cell.date)}`
                                : `Apri ${featuredShow.nome}`
                            }
                          >
                            <div className='shows-calendar-tile__center'>
                              {featuredShow?.logo ? (
                                <img
                                  src={featuredShow.logo}
                                  alt={featuredShow.nome}
                                  className='shows-calendar-tile__logo'
                                />
                              ) : (
                                <span className='shows-calendar-tile__fallback'>
                                  {(featuredShowBadge?.shortLabel || 'SHOW').toUpperCase()}
                                </span>
                              )}
                            </div>
                          </button>
                        ) : hasPlaceholder ? (
                          <div className='shows-calendar-tile shows-calendar-tile--preset'>
                            {cell.placeholder.logo && (
                              <img
                                src={cell.placeholder.logo}
                                alt={cell.placeholder.type}
                                className='shows-calendar-tile__logo shows-calendar-tile__logo--preset'
                              />
                            )}
                          </div>
                        ) : (
                          <div className='shows-calendar-day__empty-state' aria-hidden='true' />
                        )}
                      </div>
                    </article>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Modal per giorni con più show */}
        {modalShow && (
          <div
            className='modal fade show d-block shows-date-modal'
            tabIndex='-1'
            role='dialog'
            aria-modal='true'
            onClick={() => setModalShow(false)}
          >
            <div
              className='modal-dialog modal-dialog-centered'
              onClick={e => e.stopPropagation()}
            >
              <div className='modal-content brand-card border-0 shows-date-modal__content'>
                <div className='modal-header shows-date-modal__header'>
                  <h5 className='modal-title fw-bold text-brand-secondary'>
                    <i className='bi bi-calendar-event me-2'></i>
                    {selectedDate ? formatFullDate(selectedDate) : ''}
                  </h5>
                  <button
                    type='button'
                    className='btn-close btn-close-white'
                    onClick={() => setModalShow(false)}
                  ></button>
                </div>
                <div className='modal-body shows-date-modal__body'>
                  {selectedShows.length === 0 ? (
                    <p className='text-muted-brand mb-0'>
                      Nessuno show per questa data
                    </p>
                  ) : (
                    <div className='shows-date-modal__list'>
                      {selectedShows.map(show => {
                        const showBadge = getShowTypeBadgeMeta(show.type)

                        return (
                          <Link
                            key={show.id}
                            to={`/shows/${show.id}`}
                            className='shows-date-modal__show text-decoration-none'
                            onClick={() => setModalShow(false)}
                            aria-label={`Apri ${show.nome}`}
                            style={{
                              backgroundImage: show.sfondo
                                ? `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4)), url(${show.sfondo})`
                                : 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-dark))',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          >
                            <div className='shows-date-modal__show-content'>
                              {show.logo && (
                                <img
                                  src={show.logo}
                                  alt=''
                                  className='shows-date-modal__show-logo'
                                />
                              )}
                              {show.isExtra && (
                                <span className='badge bg-brand-accent shows-date-modal__show-extra'>
                                  <i className='bi bi-star-fill me-1'></i>
                                  Extra
                                </span>
                              )}
                              <div className='shows-date-modal__show-meta'>
                                <span className={`badge ${showBadge.className}`}>
                                  {showBadge.label}
                                </span>
                                <span className='shows-date-modal__show-count'>
                                  <i className='bi bi-people me-1'></i>
                                  {show.participantsCount || 0}
                                </span>
                              </div>
                            </div>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <SectionAd
          placement='home'
          className='d-flex justify-content-center my-5'
        />
      </div>
    </div>
  )
}

export default Shows
